'use client';

import { Fragment, useEffect, useState } from 'react';

import dayjs from 'dayjs';

import { BookingResponse } from '@/components/event-payment/types';
import { useTranslation } from '@/i18n/client';
import { EventDetails } from '@/types/events';
import { WithLang } from '@/types/general';
import { CurrencyCode } from '@/types/general/base';
import { priceToFixed } from '@/utils/number-utils';

interface Props {
    bookingResponse?: BookingResponse | null;
    onSlideBack: () => void;
    event: EventDetails;
    currencyCode: CurrencyCode;
}

export default function EventBookingResult(props: WithLang<Props>) {
    const { t: tPayments } = useTranslation(props.lang, 'payments');
    const [redirectTimer, setRedirectTimer] = useState(20);

    const bookTitle = props.bookingResponse?.bookingTimeInterval
        ? tPayments('messages.booked.title', {
              returnObjects: true,
              book_time: props?.bookingResponse?.bookingTimeInterval / 60,
          })
        : '';

    const bookDesc = tPayments('messages.booked.desc', {
        returnObjects: true,
        counter: redirectTimer,
        payment_link: props.bookingResponse?.paymentGatewayUrl,
    });

    const bookData = [
        {
            title: tPayments('common.event_name'),
            value: props?.event?.name,
        },
        {
            title: tPayments('common.event_start_date'),
            value: dayjs(props?.event?.start_date).format('DD-MM-YYYY HH:mm'),
        },
        {
            title: tPayments('tickets.quantity'),
            value: props.bookingResponse?.ticketsQuantity,
        },
        // {
        //     title: tPayments('tickets.type'),
        //     value: `\n${props.ticketList}` || props.ticketOption?.description || props.ticketOption?.tag,
        // },
        {
            title: tPayments('common.final_price'),
            value: `${priceToFixed(props.bookingResponse?.finalPrice)} ${props.currencyCode}`,
            accent: true,
        },
    ];

    useEffect(() => {
        if (props.bookingResponse?.bookingTimeInterval) {
            const interval = setInterval(() => {
                setRedirectTimer((prevTimer) => prevTimer - 1);
            }, 1000);

            if (redirectTimer === 0) {
                clearInterval(interval);
                window.open(props.bookingResponse?.paymentGatewayUrl, '_self');
            }

            return () => clearInterval(interval);
        }
    }, [redirectTimer, props.bookingResponse]);

    return (
        <div className="mx-auto flex w-full min-w-full flex-col items-center justify-center">
            {props.bookingResponse?.paymentGatewayUrl ? (
                <div className="mx-auto mt-8 w-full md:mt-0 md:w-1/2">
                    <h3
                        className="mb-4 whitespace-pre text-center text-lg font-bold md:text-2xl"
                        dangerouslySetInnerHTML={{ __html: bookTitle }}
                    />
                    <div className="h-40 w-full bg-tickets_success bg-contain bg-center bg-no-repeat md:h-80" />
                    <p className="text-regular text-center md:text-lg" dangerouslySetInnerHTML={{ __html: bookDesc }} />

                    <div className="text-regular mx-auto mt-8 flex flex-col justify-center text-center md:text-lg">
                        <div className="mx-auto flex flex-col justify-center">
                            {bookData.map(
                                (item, index) =>
                                    item.value && (
                                        <Fragment key={index}>
                                            <span className={`whitespace-pre-line text-center`}>
                                                {item.title}:<span className="font-bold"> {item.value}</span>
                                            </span>
                                        </Fragment>
                                    ),
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="mx-auto mt-8 w-full md:mt-0 md:w-1/2">
                    <h3 className="text-center text-lg md:text-2xl">{tPayments('messages.internal.title')}</h3>
                    <div className="h-40 w-full bg-tickets_internal bg-contain bg-center bg-no-repeat md:h-80" />
                    <p className="text-regular text-center md:text-lg">{tPayments('messages.internal.desc')}</p>
                </div>
            )}
        </div>
    );
}
